import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { hrApi } from '@/api';
import { queryKeys } from '@/lib/query';
import { hrFormatAttendanceClock, hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';
import { cn } from '@/utils/cn';
import type { HrAttendanceSegment } from '@/types/hr';

const SEGMENT_STYLES: Record<string, string> = {
  REGULAR: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  OVERTIME_BEFORE_SHIFT: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  OVERTIME_BETWEEN_SHIFTS: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  OVERTIME_AFTER_SHIFT: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  OVERTIME_UNSCHEDULED: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  BREAK: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300',
};

/** Normalized row for both saved segments and pre-save estimates. */
export type HrAttendanceSegmentView = {
  key: string;
  segmentType: string;
  start: string;
  end: string;
  minutes: number;
  breakMinutes?: number;
  overtime?: boolean;
  overtimeType?: string | null;
  shiftName?: string | null;
};

function segmentMinutes(segment: HrAttendanceSegment): number {
  return segment.segmentType === 'BREAK' ? segment.breakMinutes : segment.minutes;
}

export function toAttendanceSegmentViews(segments: HrAttendanceSegment[]): HrAttendanceSegmentView[] {
  return segments.map((segment) => ({
    key: String(segment.id),
    segmentType: segment.segmentType,
    start: segment.segmentStart,
    end: segment.segmentEnd,
    minutes: segmentMinutes(segment),
    breakMinutes: segment.breakMinutes,
    overtime: segment.overtime,
    overtimeType: segment.overtimeType,
    shiftName: segment.shiftName,
  }));
}

/**
 * Colored chip list used by the calendar day popup and every approval timeline.
 * Pass already-normalized rows (saved or estimated). Always shows when there is at least
 * one slice so check-in/out worked time stays visible end-to-end.
 */
export function HrAttendanceSegmentList({
  segments,
  className,
  showTitle = true,
}: {
  segments: HrAttendanceSegmentView[];
  className?: string;
  showTitle?: boolean;
}) {
  const { t } = useTranslation('hr');

  if (segments.length === 0) return null;

  const regularMinutes = segments
    .filter((segment) => segment.segmentType === 'REGULAR')
    .reduce((total, segment) => total + segment.minutes, 0);
  const overtimeSegments = segments.filter(
    (segment) => segment.overtime || segment.segmentType.startsWith('OVERTIME'),
  );
  const overtimeMinutes = overtimeSegments.reduce((total, segment) => total + segment.minutes, 0);
  const breakMinutes = segments
    .filter((segment) => segment.segmentType === 'BREAK')
    .reduce((total, segment) => total + segment.minutes, 0);

  return (
    <div className={cn('mt-3 space-y-1.5', className)}>
      {showTitle ? (
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
            {t('attendance.segments.title')}
          </p>
          {overtimeSegments.length > 0 && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              {t('attendance.segments.splitCount', { count: overtimeSegments.length })}
            </p>
          )}
        </div>
      ) : null}
      <ul className="space-y-1">
        {segments.map((segment) => (
          <li
            key={segment.key}
            className={cn(
              'flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 rounded-md px-2.5 py-1.5 text-xs',
              SEGMENT_STYLES[segment.segmentType] ?? SEGMENT_STYLES.REGULAR,
            )}
          >
            <span className="font-medium tabular-nums">
              {hrFormatAttendanceClock(segment.start)} → {hrFormatAttendanceClock(segment.end)}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              <span>{t(`attendance.segments.types.${segment.segmentType}`, segment.segmentType)}</span>
              {segment.segmentType !== 'BREAK' && (segment.breakMinutes ?? 0) > 0 ? (
                <span className="opacity-70">
                  · {t('attendance.segments.includesBreak', { break: hrFormatMinutes(segment.breakMinutes!) })}
                </span>
              ) : null}
              {segment.shiftName ? <span className="opacity-70">· {segment.shiftName}</span> : null}
              {segment.overtime && segment.overtimeType ? (
                <span className="opacity-70">· {segment.overtimeType}</span>
              ) : null}
              <span className="font-semibold tabular-nums">{hrFormatMinutes(segment.minutes)}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {breakMinutes > 0
          ? t('attendance.segments.summaryWithBreak', {
              regular: hrFormatMinutes(regularMinutes),
              overtime: hrFormatMinutes(overtimeMinutes),
              break: hrFormatMinutes(breakMinutes),
            })
          : t('attendance.segments.summary', {
              regular: hrFormatMinutes(regularMinutes),
              overtime: hrFormatMinutes(overtimeMinutes),
            })}
      </p>
    </div>
  );
}

/**
 * Shows how a punch was cut against the scheduled shift: the hours worked inside the shift and
 * each overtime slice worked before, between or after it, with its own clock times.
 */
export function HrAttendanceSegmentBreakdown({
  attendanceId,
  enabled = true,
  className,
}: {
  attendanceId: number;
  enabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('hr');
  const { data: segments = [], isLoading } = useQuery({
    queryKey: queryKeys.hr.attendanceSegments(attendanceId),
    queryFn: () => hrApi.listAttendanceSegments(attendanceId),
    enabled: enabled && attendanceId > 0,
  });

  if (!enabled) return null;

  if (isLoading) {
    return (
      <p className={cn('mt-3 text-xs text-slate-500 dark:text-slate-400', className)}>
        {t('attendance.segments.loading')}
      </p>
    );
  }

  return (
    <HrAttendanceSegmentList
      segments={toAttendanceSegmentViews(segments)}
      className={className}
    />
  );
}
